import { createContext, useCallback, useContext, useRef, useState, useEffect } from "react";
import { getCallSession } from "@service/aiqm";
import type { CallSessionStatus, AiqmUploadWSProgressMessage } from "@type/aiqm";
import { TERMINAL_CALL_STATUSES } from "@type/aiqm";

// Persist the in-flight upload so the progress toast survives a full page
// reload (F5). We store it in sessionStorage — it outlives a refresh but is
// cleared when the tab closes, which is the right lifetime for a transient
// upload: on reload the training-aiqm page re-opens the user-scoped progress
// WebSocket (its `isTrackingUpload` effect fires once `uploadState` rehydrates)
// and any per-file events missed during the reload gap are recovered by the
// one-shot reconcile below. We deliberately DON'T persist the pure "uploading"
// phase (no sessions yet — the upload POST hadn't returned batch/session ids,
// so there's nothing to reconnect to).
const STORAGE_KEY = "aiqm_upload_state_v1";

interface PersistedUpload {
    uploadState: AiqmUploadState;
    isToastVisible: boolean;
}

function readPersistedUpload(): PersistedUpload | null {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw) as PersistedUpload;
        if (!parsed?.uploadState || !Array.isArray(parsed.uploadState.sessions)) {
            return null;
        }
        return parsed;
    } catch {
        return null;
    }
}

// Only worth persisting once the batch has real sessions to reconnect/reconcile
// to. The pre-session "uploading" phase is unrecoverable across a reload.
function shouldPersist(state: AiqmUploadState | null): boolean {
    return !!state && state.sessions.length > 0;
}

export type AiqmUploadPhase = "uploading" | "analyzing" | "done" | "failed";

export type AiqmSessionErrorType = "system_error" | "not_cs_call" | "duplicate" | "cancelled";

export interface AiqmUploadSession {
    sessionId: string;
    filename: string;
    serverStatus: CallSessionStatus;
    errorMessage?: string;
    errorType?: AiqmSessionErrorType;
}

// Classify a failed upload into a UI bucket, mirroring the bulk-CV toast: the
// WS `status` distinguishes cancelled/duplicate outright; otherwise the BE
// `message` text decides "Not a CS Call" vs a generic system error.
function classifyAiqmError(
    status?: AiqmUploadWSProgressMessage["status"],
    message?: string | null,
): AiqmSessionErrorType {
    if (status === "cancelled") return "cancelled";
    if (status === "duplicate") return "duplicate";
    const text = (message ?? "").toLowerCase();
    if (
        text.includes("not a cs") ||
        text.includes("not a customer service") ||
        text.includes("not a valid cs") ||
        text.includes("not a valid call") ||
        text.includes("not a call")
    ) {
        return "not_cs_call";
    }
    return "system_error";
}

export interface AiqmUploadState {
    pendingFilenames: string[];
    sessions: AiqmUploadSession[];
    phase: AiqmUploadPhase;
    errorMessage?: string;
    // `batchId` ties live progress events to this upload (#2517). Progress is
    // derived from the session counts (like bulk-CV), not a WS current/total.
    batchId?: string | null;
}

interface AiqmUploadContextValue {
    seedErrorPreview?: () => void;
    uploadState: AiqmUploadState | null;
    isToastVisible: boolean;
    isProcessing: boolean;
    toastFlashCount: number;
    beginUpload: (filenames: string[]) => void;
    attachSessions: (sessions: { sessionId: string; filename: string; status: CallSessionStatus }[], batchId?: string | null) => void;
    applyWsProgress: (msg: AiqmUploadWSProgressMessage) => void;
    markUploadFailed: (message: string) => void;
    dismissToast: () => void;
    removeSession: (sessionId: string) => void;
    flashToast: () => void;
}

const AiqmUploadContext = createContext<AiqmUploadContextValue | null>(null);

function derivePhase(sessions: AiqmUploadSession[]): "analyzing" | "done" {
    const allTerminal = sessions.every((s) =>
        TERMINAL_CALL_STATUSES.includes(s.serverStatus),
    );
    return allTerminal ? "done" : "analyzing";
}

// Map a per-file WS `status` to our CallSessionStatus (updated BE contract,
// #3575/#3576/#3579). `event` is the BATCH signal and is handled separately —
// the file's tick is derived ONLY from `status`:
//   completed / success            → results_ready (green tick)
//   failed / cancelled / duplicate → failed (red)
//   transcribing / scoring / processing → still in flight (spinner + sub-status)
// The pre-contract bug was treating EVERY non-failure status as done, which made
// an in-progress `transcribing`/`scoring` frame flip a file to a green tick (and
// the batch to "done") prematurely.
function wsStatusToServerStatus(
    status: AiqmUploadWSProgressMessage["status"],
): CallSessionStatus {
    switch (status) {
        case "completed":
        case "success":
            return "results_ready";
        case "failed":
        case "cancelled":
        case "duplicate":
            return "failed";
        case "transcribing":
            return "transcribing";
        case "scoring":
            return "scoring";
        default: // "processing" (or any unknown in-flight value)
            return "analyzing";
    }
}

// Pure: apply a single WS event to an upload state and return the next state. If
// the event's session row isn't attached yet, the state is returned UNCHANGED —
// the caller keeps it buffered for a later replay.
function applyProgressToState(
    state: AiqmUploadState,
    msg: AiqmUploadWSProgressMessage,
): AiqmUploadState {
    // Correlate by the backend `session_id` ALONE (a globally-unique UUID), like
    // the bulk-CV toast matches by `temp_cv_id`. We deliberately do NOT also gate
    // on batch_id: the user-scoped socket only carries this user's events and
    // session ids never collide across batches, so the session id is a sufficient
    // key. Requiring batch_id to equal the upload-response value silently drops
    // every event if the BE labels the batch differently in the POST response vs.
    // the WS stream — which leaves uploads stuck in "analyzing" forever.
    if (!state.sessions.some((s) => s.sessionId === msg.session_id)) return state;
    const serverStatus = wsStatusToServerStatus(msg.status);
    const isFailure = serverStatus === "failed";
    const isInFlight = !TERMINAL_CALL_STATUSES.includes(serverStatus);
    const sessions: AiqmUploadSession[] = state.sessions.map((s) => {
        // Match on the backend-issued session_id only — filenames can collide
        // within a batch, so an OR-match on filename would flip the wrong file.
        if (s.sessionId !== msg.session_id) return s;
        if (isFailure) {
            // Prefer the BE-streamed reason (mirrors how the bulk-CV toast reads
            // `msg.message`); fall back to any existing message, then a default.
            const errorMessage = msg.message ?? s.errorMessage ?? "Analysis failed";
            return { ...s, serverStatus, errorMessage, errorType: classifyAiqmError(msg.status, msg.message) };
        }
        // Don't let a late/out-of-order in-flight frame (`processing`) un-complete
        // a file that already reached a terminal state.
        if (isInFlight && TERMINAL_CALL_STATUSES.includes(s.serverStatus)) return s;
        return { ...s, serverStatus };
    });
    // `event` is the BATCH signal, orthogonal to per-file `status`:
    // "completed" means every file was handled → show the completion banner even
    // if the last file failed. Otherwise derive the phase from the rows.
    const phase: AiqmUploadPhase = msg.event === "completed" ? "done" : derivePhase(sessions);
    return { ...state, sessions, phase };
}

// Apply an authoritative session status fetched from the REST API (used by the
// post-reload reconcile). Mirrors applyProgressToState, but the source is a
// CallSession.status rather than a WS event. Non-terminal statuses are left
// untouched — the reconnected WS will deliver their terminal event.
function applyReconciledStatus(
    state: AiqmUploadState,
    sessionId: string,
    status: CallSessionStatus,
    errorMessage?: string | null,
): AiqmUploadState {
    if (!TERMINAL_CALL_STATUSES.includes(status)) return state;
    const isFailure = status === "failed" || status === "cancelled";
    const sessions: AiqmUploadSession[] = state.sessions.map((s) => {
        if (s.sessionId !== sessionId) return s;
        if (isFailure) {
            return {
                ...s,
                serverStatus: "failed",
                errorMessage: errorMessage ?? s.errorMessage ?? "Analysis failed",
                errorType: classifyAiqmError(status === "cancelled" ? "cancelled" : "failed", errorMessage),
            };
        }
        return { ...s, serverStatus: "results_ready" };
    });
    return { ...state, sessions, phase: derivePhase(sessions) };
}

export function AiqmUploadProvider({ children }: { children: React.ReactNode }) {
    // Rehydrate the toast from sessionStorage exactly once, so a page reload
    // (F5) mid-upload doesn't lose the in-flight progress (read once via a ref
    // so re-renders don't re-parse storage).
    const restoredRef = useRef<PersistedUpload | null | undefined>(undefined);
    if (restoredRef.current === undefined) restoredRef.current = readPersistedUpload();
    const restored = restoredRef.current;

    const [uploadState, setUploadState] = useState<AiqmUploadState | null>(
        restored?.uploadState ?? null,
    );
    const [isToastVisible, setIsToastVisible] = useState(restored?.isToastVisible ?? false);
    const [toastFlashCount, setToastFlashCount] = useState(0);
    // Buffers WS events that arrive before their session is attached. The socket
    // is opened before the upload POST resolves, so a fast file can stream its
    // terminal event in the window between connect and attachSessions; the drain
    // effect replays them once the matching session exists, so the first terminal
    // event is never lost (there is no polling fallback to recover a dropped one).
    const pendingWsEventsRef = useRef<AiqmUploadWSProgressMessage[]>([]);

    // Drain buffered WS events on every commit: once a batch's sessions are
    // attached (a commit from attachSessions/applySessions), replay any
    // events that raced ahead and were buffered, applying the ones whose session
    // now exists. Each applied event removes itself from the buffer; the setState
    // triggers one more commit that finds the buffer empty, so this converges.
    useEffect(() => {
        if (!uploadState) return;
        const queued = pendingWsEventsRef.current;
        if (queued.length === 0) return;
        const ready: AiqmUploadWSProgressMessage[] = [];
        const stillPending: AiqmUploadWSProgressMessage[] = [];
        for (const m of queued) {
            if (uploadState.sessions.some((s) => s.sessionId === m.session_id)) ready.push(m);
            else stillPending.push(m);
        }
        if (ready.length === 0) return;
        pendingWsEventsRef.current = stillPending;
        setUploadState((prev) =>
            prev ? ready.reduce((st, m) => applyProgressToState(st, m), prev) : prev,
        );
    }, [uploadState]);

    // Persist the toast to sessionStorage so it survives a page reload. Written
    // on every meaningful change (each WS event) and cleared when the upload is
    // dismissed (uploadState → null) or is a not-yet-tracked "uploading" state.
    useEffect(() => {
        try {
            if (isToastVisible && shouldPersist(uploadState)) {
                sessionStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify({ uploadState, isToastVisible }),
                );
            } else {
                sessionStorage.removeItem(STORAGE_KEY);
            }
        } catch {
            // sessionStorage can throw (private mode / quota) — persistence is a
            // best-effort enhancement, never block the upload on it.
        }
    }, [uploadState, isToastVisible]);

    // One-shot post-reload reconcile: if we rehydrated with sessions still
    // in-flight, a terminal WS event may have fired during the reload gap and
    // been missed (the socket was closed). Fetch the authoritative status for
    // each non-terminal session once so a finished file can't stay stuck in
    // "analyzing" forever. Sessions still processing are left for the
    // reconnected WS to complete.
    useEffect(() => {
        const nonTerminal = (restored?.uploadState.sessions ?? []).filter(
            (s) => !TERMINAL_CALL_STATUSES.includes(s.serverStatus),
        );
        if (nonTerminal.length === 0) return;
        let cancelled = false;
        (async () => {
            for (const s of nonTerminal) {
                try {
                    const cs = await getCallSession(s.sessionId);
                    if (cancelled) return;
                    setUploadState((prev) =>
                        prev ? applyReconciledStatus(prev, s.sessionId, cs.status, cs.error_message) : prev,
                    );
                } catch {
                    // Ignore — the reconnected WS remains the primary path; a
                    // failed reconcile just leaves the row for the socket.
                }
            }
        })();
        return () => {
            cancelled = true;
        };
        // Runs once on mount against the restored snapshot.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply a live event from the upload WebSocket (#2517). The WS is the SOLE
    // source of terminal per-file status (no polling fallback) — this mirrors the
    // bulk-CV upload. Correlation is by `session_id` ALONE (like bulk-CV matches
    // by temp_cv_id), NOT by batch_id — see applyProgressToState for why. The
    // decision is made INSIDE the updater against the authoritative `prev` (so an
    // event that arrives after attachSessions is queued but before it commits
    // still correlates). If the session isn't attached yet, the event is BUFFERED
    // (deduped by reference for React StrictMode's double-invoke) and replayed by
    // the drain effect once its session exists — so a fast batch's first terminal
    // event is never dropped.
    const applyWsProgress = useCallback((msg: AiqmUploadWSProgressMessage) => {
        setUploadState((prev) => {
            if (prev && prev.sessions.some((s) => s.sessionId === msg.session_id)) {
                return applyProgressToState(prev, msg);
            }
            if (!pendingWsEventsRef.current.includes(msg)) pendingWsEventsRef.current.push(msg);
            return prev;
        });
    }, []);

    // Writes the session list to state. **No polling** — terminal per-file status
    // arrives via the upload WebSocket (`applyWsProgress`), mirroring the bulk-CV
    // upload flow. When a batchId is given (a new/retried batch) it is set on
    // state; the drain effect then replays any events buffered for that batch.
    const applySessions = useCallback(
        (newSessions: AiqmUploadSession[], keepSessions: AiqmUploadSession[], batchId?: string | null) => {
            const allSessions = [...keepSessions, ...newSessions];
            setUploadState((prev) =>
                prev
                    ? {
                          ...prev,
                          pendingFilenames: [],
                          sessions: allSessions,
                          phase: derivePhase(allSessions),
                          ...(batchId ? { batchId } : {}),
                      }
                    : prev,
            );
        },
        [],
    );

    const beginUpload = useCallback(
        (filenames: string[]) => {
            // Fresh upload session — drop any events left buffered from a prior one.
            pendingWsEventsRef.current = [];
            setUploadState({
                pendingFilenames: filenames,
                sessions: [],
                phase: "uploading",
            });
            setIsToastVisible(true);
        },
        [],
    );

    // Maps the server-returned sessions into the toast's session list.
    const attachSessions = useCallback(
        (incoming: { sessionId: string; filename: string; status: CallSessionStatus }[], batchId?: string | null) => {
            const newSessions: AiqmUploadSession[] = incoming.map((s) => ({
                sessionId: s.sessionId,
                filename: s.filename,
                serverStatus: s.status,
            }));
            // Set batchId + sessions; the drain effect replays any buffered events.
            applySessions(newSessions, [], batchId);
        },
        [applySessions],
    );

    const markUploadFailed = useCallback((message: string) => {
        setUploadState((prev) =>
            prev ? { ...prev, phase: "failed", errorMessage: message } : prev,
        );
    }, []);

    const dismissToast = useCallback(() => {
        setIsToastVisible(false);
        setUploadState(null);
        pendingWsEventsRef.current = [];
    }, []);

    const removeSession = useCallback((sessionId: string) => {
        setUploadState((prev) =>
            prev ? { ...prev, sessions: prev.sessions.filter((s) => s.sessionId !== sessionId) } : prev,
        );
    }, []);

    const flashToast = useCallback(() => {
        setToastFlashCount((c) => c + 1);
    }, []);

    const seedErrorPreview = import.meta.env.DEV
        ? () => {
              setUploadState({
                  pendingFilenames: [],
                  sessions: [
                      {
                          sessionId: "preview-ok-1",
                          filename: "call_recording_01.mp3",
                          serverStatus: "results_ready",
                      },
                      {
                          sessionId: "preview-sys-1",
                          filename: "call_recording_02.mp3",
                          serverStatus: "failed",
                          errorMessage: "Internal server error during transcription",
                          errorType: "system_error",
                      },
                      {
                          sessionId: "preview-sys-2",
                          filename: "call_recording_03.wav",
                          serverStatus: "failed",
                          errorMessage: "Network timeout during analysis",
                          errorType: "system_error",
                      },
                      {
                          sessionId: "preview-ncs-1",
                          filename: "background_music.mp3",
                          serverStatus: "failed",
                          errorMessage: "Not a CS call: audio does not contain a valid customer service conversation",
                          errorType: "not_cs_call",
                      },
                      {
                          sessionId: "preview-ncs-2",
                          filename: "one_way_call.m4a",
                          serverStatus: "failed",
                          errorMessage: "Not a CS call: only one speaker detected",
                          errorType: "not_cs_call",
                      },
                  ],
                  phase: "done",
              });
              setIsToastVisible(true);
          }
        : undefined;

    const isProcessing =
        uploadState !== null &&
        (uploadState.phase === "uploading" || uploadState.phase === "analyzing");

    return (
        <AiqmUploadContext.Provider
            value={{
                uploadState,
                isToastVisible,
                isProcessing,
                toastFlashCount,
                beginUpload,
                attachSessions,
                applyWsProgress,
                markUploadFailed,
                dismissToast,
                removeSession,
                flashToast,
                seedErrorPreview,
            }}
        >
            {children}
        </AiqmUploadContext.Provider>
    );
}

export function useAiqmUpload(): AiqmUploadContextValue {
    const ctx = useContext(AiqmUploadContext);
    if (!ctx) throw new Error("useAiqmUpload must be used within AiqmUploadProvider");
    return ctx;
}
